import { computed, toValue, type MaybeRef } from 'vue'

export const useWysiwygContent = (rawContent: MaybeRef<any>) => {
  const { locale } = useI18n()
  const localePath = useLocalePath()

  const formattedContent = computed(() => {
    const content = toValue(rawContent)
    if (!content) return ''

    let htmlString = ''

    if (typeof content === 'string') {
      htmlString = content
    } else if (typeof content === 'object' && !Array.isArray(content)) {
      const currentLang = locale.value?.split('-')[0] || 'cs'
      const localizedVal =
        content[locale.value] ||
        content[currentLang] ||
        content.cs ||
        Object.values(content)[0] ||
        ''

      if (typeof localizedVal === 'string') {
        htmlString = localizedVal
      } else if (Array.isArray(localizedVal)) {
        htmlString = processBlocks(localizedVal, currentLang, localePath)
      }
    } else if (Array.isArray(content)) {
      const lang = locale.value?.split('-')[0] || 'cs'
      htmlString = processBlocks(content, lang, localePath)
    }

    if (!htmlString) return ''

    return htmlString.replace(/<p>\s*(?:&nbsp;)?\s*<\/p>/gi, '<p><br /></p>')
  })

  const pCount = computed(() => {
    return (formattedContent.value.match(/<p[\s>]/gi) || []).length
  })

  const handleLinkClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement
    const a = target.closest('a')
    if (a && a.href) {
      try {
        const url = new URL(a.href, window.location.origin)
        if (url.origin === window.location.origin && a.target !== '_blank') {
          e.preventDefault()
          navigateTo(url.pathname + url.search + url.hash)
        }
      } catch {
        // ignore
      }
    }
  }

  return {
    formattedContent,
    pCount,
    handleLinkClick,
  }
}

function processBlocks(
  blocks: any[],
  locale: string = 'cs',
  localePath: (path: string) => string = (p) => p,
): string {
  return blocks
    .map((block) => {
      if (!block || !block.__typename) return ''

      if (block.__typename === 'RichContentHtml' || block.html) {
        return block.html || ''
      }

      if (block.__typename === 'RichContentMediaLibrary') {
        const media = block.media
        const url = media?.url || media?.media?.url || ''
        if (!url) return ''
        const alt = block.alt || media?.alt_text || ''

        return `<figure class="wysiwyg-media my-6 w-full"><img src="${url}" alt="${alt}" class="h-auto w-full rounded object-cover" loading="lazy" /></figure>`
      }

      if (block.__typename === 'RichContentContentLink') {
        const title =
          block.title?.[locale] ||
          block.title?.cs ||
          (Object.values(block.title || {}) as string[])[0] ||
          ''
        if (!title) return ''

        const rawHref = block.url || block.link || '#'
        const isExternal = block.type === 'external'
        const href = isExternal ? rawHref : localePath(rawHref)
        const target = isExternal ? '_blank' : (block.target || '_self')
        return `<p><a href="${href}" target="${target}">${title}</a></p>`
      }

      if (block.__typename === 'RichContentYoutubeEmbed') {
        let embedUrl = block.embed_url || ''
        if (!embedUrl && block.video_url) {
          const videoId = block.video_url.match(
            /(?:v=|\/embed\/|\/watch\?v=)([^&?#/]+)/,
          )?.[1]
          if (videoId) embedUrl = `https://www.youtube.com/embed/${videoId}`
        }
        if (!embedUrl) return ''

        return `<div class="wysiwyg-youtube relative my-6 w-full" style="padding-bottom:56.25%"><iframe src="${embedUrl}" title="YouTube video player" class="absolute inset-0 h-full w-full rounded" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe></div>`
      }

      return ''
    })
    .filter(Boolean)
    .join('\n')
}
